import React, { useState, useEffect } from 'react';
import { trabajosRealizados } from '../data/mockData';
import { Card } from './ui/card';
import { ChevronLeft, ChevronRight, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from './ui/button';

const TrabajoImageCarousel = ({ imagenes, titulo }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [isPaused, setIsPaused] = useState(false);

  // Mínimo de distancia para considerar swipe
  const minSwipeDistance = 50;
  
  // Tiempo entre imágenes (4 segundos)
  const autoplayDelay = 4000;

  // Autoplay
  useEffect(() => {
    if (!imagenes || imagenes.length <= 1 || isPaused) return;
    
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % imagenes.length);
    }, autoplayDelay);
    
    return () => clearInterval(interval);
  }, [imagenes, isPaused]);

  if (!imagenes || imagenes.length === 0) {
    return (
      <div className="w-full h-full bg-soft-gray flex items-center justify-center">
        <span className="text-warm-gray">Sin imagen</span>
      </div>
    );
  }

  const nextImage = (e) => {
    if (e) e.stopPropagation();
    setCurrentImageIndex((prev) => (prev + 1) % imagenes.length);
  };

  const prevImage = (e) => {
    if (e) e.stopPropagation();
    setCurrentImageIndex((prev) => (prev - 1 + imagenes.length) % imagenes.length);
  };

  // Handlers para swipe
  const onTouchStart = (e) => {
    setIsPaused(true);
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) {
      // Reanudar después de un delay si no hubo swipe
      setTimeout(() => setIsPaused(false), 3000);
      return;
    }
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe) {
      nextImage();
    } else if (isRightSwipe) {
      prevImage();
    }
    
    // Reanudar autoplay después de interacción
    setTimeout(() => setIsPaused(false), 3000);
  };

  // Pausar en hover (PC)
  const handleMouseEnter = () => setIsPaused(true);
  const handleMouseLeave = () => setIsPaused(false);

  // Click en flechas pausa temporalmente
  const handleArrowClick = (e, direction) => {
    setIsPaused(true);
    if (direction === 'prev') {
      prevImage(e);
    } else {
      nextImage(e);
    }
    setTimeout(() => setIsPaused(false), 3000);
  };

  return (
    <div 
      className="relative overflow-hidden aspect-square"
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <img
        src={imagenes[currentImageIndex]}
        alt={`${titulo} - ${currentImageIndex + 1}`}
        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
        onError={(e) => {
          e.target.style.display = 'none';
          e.target.parentElement.style.backgroundColor = '#F5F3F0';
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-charcoal/80 via-charcoal/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

      {/* Flechas de navegación - solo si hay más de 1 imagen */}
      {imagenes.length > 1 && (
        <>
          <button
            onClick={(e) => handleArrowClick(e, 'prev')}
            className="carousel-nav-btn absolute left-3 top-1/2 bg-cream-white/90 text-charcoal p-2 rounded-full z-20 shadow-lg opacity-70 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity duration-300 active:bg-cream-white select-none"
            aria-label="Imagen anterior"
          >
            <ChevronLeft size={24} />
          </button>
          <button
            onClick={(e) => handleArrowClick(e, 'next')}
            className="carousel-nav-btn absolute right-3 top-1/2 bg-cream-white/90 text-charcoal p-2 rounded-full z-20 shadow-lg opacity-70 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity duration-300 active:bg-cream-white select-none"
            aria-label="Siguiente imagen"
          >
            <ChevronRight size={24} />
          </button>

          {/* Indicadores de posición */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-20 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity duration-300">
            {imagenes.map((_, idx) => (
              <div
                key={idx}
                className={`h-2 rounded-full transition-all duration-300 ${
                  idx === currentImageIndex
                    ? 'bg-primary-pink w-8'
                    : 'bg-cream-white/70 w-2'
                }`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const Gallery = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  // Mostrar solo 3 trabajos inicialmente, todos si está expandido
  const displayedTrabajos = isExpanded ? trabajosRealizados : trabajosRealizados.slice(0, 3);
  return (
    <section id="trabajos" className="py-24 bg-soft-gray">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <p className="text-primary-pink text-sm tracking-widest uppercase mb-4 font-medium">
            Casos de Éxito
          </p>
          <h2 className="text-4xl md:text-5xl font-serif text-charcoal mb-6">
            Nuestros Trabajos
          </h2>
          <div className="w-24 h-1 bg-primary-pink mx-auto mb-6"></div>
          <p className="text-warm-gray text-lg max-w-2xl mx-auto">
            Cada diseño cuenta una historia. Descubre algunos de nuestros proyectos más destacados.
          </p>
        </div>

        {/* Gallery Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {displayedTrabajos.map((trabajo) => (
            <Card
              key={trabajo.id}
              className="group overflow-hidden border-none shadow-lg hover:shadow-2xl transition-all duration-500 bg-white"
            >
              <TrabajoImageCarousel imagenes={trabajo.imagenes} titulo={trabajo.titulo} />

              {/* Card Footer - Always Visible */}
              <div className="p-6">
                <h3 className="text-lg font-serif text-charcoal mb-1 group-hover:text-primary-pink transition-colors duration-300">
                  {trabajo.titulo}
                </h3>
                <p className="text-warm-gray text-sm">
                  {trabajo.descripcion}
                </p>
              </div>
            </Card>
          ))}
        </div>

        {/* Botón Ver Más / Ver Menos */}
        {trabajosRealizados.length > 3 && (
          <div className="mt-12 text-center">
            <Button
              onClick={() => setIsExpanded(!isExpanded)}
              className="bg-primary-pink hover:bg-deep-navy text-cream-white px-8 py-4 rounded-full font-medium tracking-wide transition-all duration-300 shadow-lg hover:shadow-xl flex items-center gap-3 mx-auto"
            >
              {isExpanded ? (
                <>
                  <span>VER MENOS TRABAJOS</span>
                  <ChevronUp size={24} className="animate-bounce" />
                </>
              ) : (
                <>
                  <span>VER MÁS TRABAJOS</span>
                  <ChevronDown size={24} className="animate-bounce" />
                </>
              )}
            </Button>
            <p className="text-warm-gray text-sm mt-3">
              {isExpanded 
                ? `Mostrando ${trabajosRealizados.length} de ${trabajosRealizados.length} trabajos`
                : `Mostrando 3 de ${trabajosRealizados.length} trabajos`
              }
            </p>
          </div>
        )}

        {/* CTA Section */}
        <div className="mt-16 text-center">
          <Card className="bg-white border-none p-12 shadow-xl max-w-3xl mx-auto">
            <h3 className="text-3xl font-serif text-charcoal mb-4">
              ¿Listo para tu diseño único?
            </h3>
            <p className="text-warm-gray mb-8 text-lg">
              Tu mente es el límite. Trabajamos contigo para crear el diseño perfecto que refleje tu estilo.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://wa.me/59898836023"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center bg-primary-pink hover:bg-deep-navy text-cream-white px-8 py-3 rounded font-medium tracking-wide transition-colors duration-300"
              >
                CONSULTAR POR WHATSAPP
              </a>
              <button
                onClick={() => {
                  const element = document.getElementById('cotizacion');
                  if (element) element.scrollIntoView({ behavior: 'smooth' });
                }}
                className="inline-flex items-center justify-center border-2 border-primary-pink text-primary-pink hover:bg-primary-pink hover:text-cream-white px-8 py-3 rounded font-medium tracking-wide transition-all duration-300"
              >
                SOLICITAR COTIZACIÓN
              </button>
            </div>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default Gallery;
